import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, XCircle, RefreshCw, Zap, Info, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";

const RAZORPAY_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (typeof window !== "undefined" && window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

function StatusPill({ item }) {
  // Compose the overall status from signature + webhook flags.
  const sig = item.signature_valid;
  const webhook = !!item.webhook_received_at;
  if (sig === true && webhook) {
    return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">✓ Sig + Webhook</Badge>;
  }
  if (sig === true && !webhook) {
    return <Badge className="bg-amber-100 text-amber-800 border border-amber-200">Sig OK · Webhook pending</Badge>;
  }
  if (sig === false) {
    return <Badge className="bg-red-100 text-red-800 border border-red-200">Signature FAILED</Badge>;
  }
  if (item.status === "created") {
    return <Badge className="bg-slate-100 text-slate-700 border border-slate-200">Awaiting payment</Badge>;
  }
  return <Badge variant="outline">{item.status}</Badge>;
}

export default function AdminRazorpayLiveTest() {
  const [history, setHistory] = useState({ items: [], current_mode: "test", webhook_configured: false, key_id_masked: "" });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [running, setRunning] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const r = await api.get("/admin/razorpay/live-sanity/history");
      setHistory(r.data);
    } catch (e) {
      toast.error("Failed to load sanity check history");
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // After a payment, poll history for ~30s to catch webhook arrival.
  const pollForWebhook = useCallback(async (orderId) => {
    for (let i = 0; i < 10; i++) {
      // Poll every 3s for up to 30 seconds — Razorpay's webhook usually
      // arrives within 5–10s but can lag under load.
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const r = await api.get("/admin/razorpay/live-sanity/history");
        setHistory(r.data);
        const found = r.data.items?.find((it) => it.razorpay_order_id === orderId);
        if (found?.webhook_received_at) {
          toast.success("Webhook confirmed! Full end-to-end integration verified.");
          return;
        }
      } catch (pollErr) {
        // Transient network blip during polling — the next tick will retry.
        // Logged only in dev so we don't flood console in prod on flaky wifi.
        if (process.env.NODE_ENV !== "production") console.warn("sanity poll transient error:", pollErr);
      }
    }
    toast.message("Webhook not received yet", {
      description: "Signature verified. Webhook may still arrive — refresh in a minute to double-check.",
    });
  }, []);

  const runSanityTest = useCallback(async () => {
    if (!window.confirm("This will charge ₹1 to your card/UPI on Razorpay. Continue?")) return;
    setRunning(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) throw new Error("Could not load Razorpay checkout script.");

      // 1. Create order
      const { data: order } = await api.post("/admin/razorpay/live-sanity/create-order");
      if (!order?.order_id) throw new Error("Failed to create sanity order.");

      // 2. Open checkout
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Pune Spa Guide — Sanity Test",
        description: "₹1 Live Mode Verification (Admin)",
        order_id: order.order_id,
        theme: { color: "#1F4E3D" },
        modal: {
          ondismiss: () => {
            toast.message("Sanity test cancelled", { description: "No charge made. You can retry anytime." });
            setRunning(false);
            loadHistory();
          },
        },
        handler: async (resp) => {
          try {
            await api.post("/admin/razorpay/live-sanity/verify-payment", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Signature verified! Now polling for webhook arrival…");
            await loadHistory();
            await pollForWebhook(resp.razorpay_order_id);
          } catch (err) {
            const detail = err?.response?.data?.detail || err.message || "Verification failed.";
            toast.error(detail);
            await loadHistory();
          } finally {
            setRunning(false);
          }
        },
      });
      rzp.on("payment.failed", (event) => {
        const desc = event?.error?.description || "Payment failed.";
        toast.error(desc);
        setRunning(false);
        loadHistory();
      });
      rzp.open();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Something went wrong.";
      toast.error(detail);
      setRunning(false);
    }
  }, [loadHistory, pollForWebhook]);

  const isLive = history.current_mode === "live";
  const modeBadge = isLive
    ? <Badge className="bg-red-100 text-red-800 border border-red-200" data-testid="rzp-mode-badge">LIVE MODE</Badge>
    : <Badge className="bg-blue-100 text-blue-800 border border-blue-200" data-testid="rzp-mode-badge">TEST MODE</Badge>;

  return (
    <div data-testid="admin-razorpay-live-test">
      <h2 className="font-heading text-2xl mb-1">Razorpay Live Mode Sanity Test</h2>
      <p className="text-sm text-[#4B5563] mb-5">
        Verifies end-to-end payments: order creation → checkout → signature verification → webhook arrival.
        Charges exactly <span className="font-medium text-[#111827]">₹1</span> and does <span className="font-medium">not</span> modify any spa or plan.
      </p>

      {/* Configuration readout */}
      <div className="rounded-2xl bg-white border border-[#E7DDCF] p-4 mb-4" data-testid="rzp-config-card">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Current configuration</div>
          {modeBadge}
        </div>
        <dl className="grid sm:grid-cols-3 gap-3 text-sm">
          <div>
            <dt className="text-xs text-[#4B5563]">Key ID</dt>
            <dd className="font-mono text-[#111827] mt-0.5" data-testid="rzp-keyid">{history.key_id_masked || "(not set)"}</dd>
          </div>
          <div>
            <dt className="text-xs text-[#4B5563]">Webhook Secret</dt>
            <dd className="mt-0.5" data-testid="rzp-webhook-status">
              {history.webhook_configured
                ? <span className="text-emerald-700 inline-flex items-center gap-1"><ShieldCheck size={14}/> Configured</span>
                : <span className="text-red-700 inline-flex items-center gap-1"><XCircle size={14}/> Not configured</span>}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-[#4B5563]">Mode</dt>
            <dd className="mt-0.5 text-[#111827]">{isLive ? "Real money will be charged" : "Test cards only"}</dd>
          </div>
        </dl>
      </div>

      {/* Warning banner (only in live mode) */}
      {isLive && (
        <div className="rounded-2xl bg-red-50 border border-red-200 p-4 mb-4 flex items-start gap-3" data-testid="rzp-live-warning">
          <AlertTriangle size={18} className="text-red-700 shrink-0 mt-0.5"/>
          <div className="text-sm text-red-900">
            <div className="font-medium">You are in LIVE mode.</div>
            <div className="mt-0.5">Running the sanity test will charge ₹1 to your card/UPI. Money settles to your bank account per your Razorpay settlement schedule.</div>
          </div>
        </div>
      )}
      {!history.webhook_configured && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-4 flex items-start gap-3" data-testid="rzp-webhook-warning">
          <Info size={18} className="text-amber-700 shrink-0 mt-0.5"/>
          <div className="text-sm text-amber-900">
            <div className="font-medium">Webhook secret is not configured.</div>
            <div className="mt-0.5">You can still test signature verification, but webhook arrival won&apos;t be validated. Set <code className="bg-amber-100 px-1 rounded">RAZORPAY_WEBHOOK_SECRET</code> in your backend .env to enable full validation.</div>
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap gap-2 mb-5">
        <Button
          onClick={runSanityTest}
          disabled={running}
          className="bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-2"
          data-testid="run-sanity-test-btn"
        >
          <Zap size={14}/> {running ? "Running…" : `Run ₹1 Sanity Test (${isLive ? "LIVE" : "TEST"})`}
        </Button>
        <Button
          variant="outline"
          onClick={loadHistory}
          disabled={loadingHistory}
          className="border-[#E7DDCF] gap-2"
          data-testid="refresh-history-btn"
        >
          <RefreshCw size={14} className={loadingHistory ? "animate-spin" : ""}/> Refresh history
        </Button>
      </div>

      {/* History */}
      <div className="rounded-2xl bg-white border border-[#E7DDCF] overflow-hidden" data-testid="sanity-history-table">
        <div className="px-4 py-3 border-b border-[#E7DDCF] bg-[#F5EFE6]">
          <div className="font-medium text-[#111827]">Recent Sanity Checks</div>
          <div className="text-xs text-[#4B5563]">Latest 20 attempts, most recent first.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-white text-left text-xs uppercase tracking-wider text-[#4B5563]">
              <tr>
                <th className="p-3">Created</th>
                <th className="p-3">Order ID</th>
                <th className="p-3">Mode</th>
                <th className="p-3">Status</th>
                <th className="p-3">Verified</th>
                <th className="p-3">Webhook</th>
              </tr>
            </thead>
            <tbody>
              {(history.items || []).length === 0 && !loadingHistory && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-sm text-[#4B5563]">No sanity checks run yet. Click the button above to run your first one.</td>
                </tr>
              )}
              {(history.items || []).map((it) => (
                <tr key={it.id} className="border-t border-[#E7DDCF]" data-testid={`sanity-row-${it.razorpay_order_id}`}>
                  <td className="p-3 whitespace-nowrap text-[#4B5563]">{formatDate(it.created_at)}</td>
                  <td className="p-3 font-mono text-xs">{it.razorpay_order_id}</td>
                  <td className="p-3">
                    {it.mode === "live"
                      ? <Badge className="bg-red-50 text-red-700 border border-red-200">LIVE</Badge>
                      : <Badge className="bg-blue-50 text-blue-700 border border-blue-200">TEST</Badge>}
                  </td>
                  <td className="p-3"><StatusPill item={it}/></td>
                  <td className="p-3 whitespace-nowrap">
                    {it.verified_at
                      ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={14}/> {formatDate(it.verified_at)}</span>
                      : <span className="text-[#4B5563]">—</span>}
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    {it.webhook_received_at
                      ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={14}/> {formatDate(it.webhook_received_at)}</span>
                      : <span className="text-[#4B5563]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Help section */}
      <div className="mt-6 rounded-2xl bg-white border border-[#E7DDCF] p-4 text-sm text-[#4B5563]" data-testid="sanity-help">
        <div className="font-medium text-[#111827] mb-2">How this works</div>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Backend creates a real Razorpay Order for ₹1 (isolated in <code className="bg-[#F5EFE6] px-1 rounded">razorpay_sanity_checks</code> — never touches <code className="bg-[#F5EFE6] px-1 rounded">payments</code> or spa plans).</li>
          <li>Razorpay Checkout opens; you complete payment (test cards work in TEST mode).</li>
          <li>Backend verifies the signature server-side using your Key Secret — proves Key ID + Secret match.</li>
          <li>Razorpay fires the webhook to <code className="bg-[#F5EFE6] px-1 rounded">/api/razorpay/webhook</code>; we validate the webhook secret HMAC and stamp arrival.</li>
          <li>Both signature ✓ + webhook ✓ = end-to-end integration proven. Safe to go live.</li>
        </ol>
      </div>
    </div>
  );
}
